import { Fun } from 'widgets-for-react'
import {
  CookiebarStyle,
  CookiePlaceholderStyle,
} from '../root/cookiebar/cookiebarLayout'
import { CtaBlockStyle } from '../root/cmsPage/blocks/ctaBlockLayout'
import { ExpandBlockStyle } from '../root/cmsPage/blocks/expandBlock/expandBlockWidget'
import { FeaturedContentBlockStyle } from '../root/cmsPage/blocks/featuredContentBlockLayout'
import { LinkBlockStyle } from '../root/cmsPage/blocks/linkBlockLayout'
import { LinkBlockNestedStyle } from '../root/cmsPage/blocks/linkBlockNestedLayout'
import { LinkGridBlockStyle } from '../root/cmsPage/blocks/linkGridBlockLayout'
import { MediaBlockStyle } from '../root/cmsPage/blocks/mediaBlockLayout'
import { MediaBlockNestedStyle } from '../root/cmsPage/blocks/mediaBlockNestedLayout'
import { QuoteBlockStyle } from '../root/cmsPage/blocks/quoteBlockLayout'
import { QuoteBlockNestedStyle } from '../root/cmsPage/blocks/quoteBlockNestedLayout'
import { TextBlockStyle } from '../root/cmsPage/blocks/textBlockLayout'
import { TextBlockNestedStyle } from '../root/cmsPage/blocks/expandBlock/nestedBlocks/textBlockNestedLayout'
import { TextImageBlockStyle } from '../root/cmsPage/blocks/textImageBlockLayout'
import { RelatedContentBlockStyle } from '../root/cmsPage/blocks/relatedContentBlock/relatedContentBlockLayout'
import { DownloadBlockStyle } from '../root/cmsPage/blocks/downloadsBlockLayout'
import { IncompanyBlockStyle } from '../root/cmsPage/blocks/incompanyBlockLayout'
import { AdvertismentBlockStyle as AdvertisementBlockStyle } from '../root/cmsPage/blocks/advertisementBlockLayout'

// ----- STANDARD BUILDING BLOCKS -----

export const featuredContentBlockClasses: Fun<
  'left' | 'right',
  FeaturedContentBlockStyle
> = (imageDirection) => ({
  blockClassName: `block block--featured block--columns image--${imageDirection}`,
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--m gutter--m spacing--m',
  headerClassName: 'block__header',
  introClassName: 'block__intro',
  labelClassName: 'block__label label--m',
  titleClassName: 'block__title title--m',
  subtitleClassName: 'block__subtitle title--s',
  contentClassName: 'block__content',
  textClassName: 'block__text text--l',
  footerClassName: 'block__footer block__clickables',
  clickableWrapperClassName: 'block__clickable',
  clickableClassName: 'button button--primary',
  clickableLabelClassName: 'button__label',
  clickableIconClassName: 'undefined',
  iconId: 'undefined',
  figureClassName: 'block__visual',
  imageClassName: 'block__image',
  imageNonExpandableClassName: 'block__image--small',
  pictureClassName: 'undefined',
}) // DONE!

export const textImageBlockClasses: Fun<'left' | 'right', TextImageBlockStyle> =
  (imageDirection) => ({
    blockClassName: `block block--text-image block--columns image--${imageDirection}`,
    inviewClassName: 'block--inview',
    innerClassName: 'block__inner container--m gutter--m spacing--m',
    headerClassName: 'block__header',
    contentClassName: 'block__content',
    labelClassName: 'block__label label--m',
    titleClassName: 'block__title title--m',
    textClassName: 'block__text text--styled',
    introClassName: 'block__intro',
    figureClassName: 'block__visual',
    imageClassName: 'block__image',
    imageNonExpandableClassName: 'block__image--small',
    footerClassName: 'block__footer block__clickables',
    clickableWrapperClassName: 'block__clickable',
    clickablePrimaryClassName: 'button button--primary',
    clickableSecondaryClassName: 'button button--secondary',
    clickableLabelClassName: 'button__label',
    clickableIconClassName: 'undefined',
    iconPrimaryId: 'undefined',
    iconSecondaryId: 'undefined',
  }) // DONE!

export const textBlockClasses: TextBlockStyle = {
  blockClassName: 'block block--text',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--s gutter--m spacing--m',
  headerClassName: 'block__header',
  titleClassName: 'block__title title--m',
  contentClassName: 'block__content',
  textClassName: 'block__text text--styled',
  footerClassName: 'block__footer block__clickables',
  clickableWrapperClassName: 'block__clickable',
  clickablePrimaryClassName: 'button button--primary',
  clickableSecondaryClassName: 'button button--secondary',
  clickableLabelClassName: 'button__label',
  clickableIconClassName: 'undefined',
  iconPrimaryId: 'undefined',
  iconSecondaryId: 'undefined',
} // DONE!

export const ctaBlockClasses = (hasImage: boolean): CtaBlockStyle => ({
  blockContentWidthClassName: `block block--cta cta--cw ${
    hasImage ? 'cta--image' : 'cta--text'
  } spacing--m gutter--m`,
  blockFullWidthClassName: `block block--cta cta--fw ${
    hasImage ? 'cta--image' : 'cta--text'
  } spacing--m gutter--m`,
  inviewClassName: 'block--inview',
  innerClassName: `block__inner container--mx gutter--mx spacing--mx`,
  headerClassName: 'block__header',
  titleClassName: 'block__title title--m',
  contentClassName: 'block__content text--l',
  textClassName: 'block__intro',
  visualClassName: 'block__visual fit-object',
  imageClassName: 'block__image',
  imageNonExpandableClassName: 'block__image--small',
  footerClassName: 'block__footer block__clickables',
  clickableWrapperClassName: 'block__clickable',
  clickablePrimaryClassName: hasImage ? 'button button--primary' : 'button button--secondary',
  clickableSecondaryClassName: hasImage ? 'button button--secondary' : 'button button--secondary',
  clickableIconClassName: 'undefined',
  clickableLabelClassName: 'button__label',
  iconPrimaryId: 'undefined',
  iconSecondaryId: 'undefined',
}) // DONE!

const sharedlinkBlockClasses: LinkBlockStyle = {
  blockClassName: 'block block--links',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--s gutter--m spacing--m',
  headerClassName: 'block__header block__header--intro',
  titleClassName: 'block__title title--m',
  introClassName: 'block__intro text--l',
  contentClassName: 'block__content',
  listClassName: 'block__list links',
  itemClassName: 'list__item',
  linkClassName: 'list__link',
  linkInnerClassName: 'list__link-inner',
  linkLabelClassName: 'list__title title--s',
  linkTextClassName: 'list__subtitle',
}

export const linkBlockClasses: LinkBlockStyle = {
  ...sharedlinkBlockClasses,
  linkIconClassName: 'list__icon',
  iconInternalId: '#icon--link',
  iconExternalId: '#icon--external',
} // DONE!

export const courseLinkBlockClasses: LinkBlockStyle = {
  ...sharedlinkBlockClasses,
  linkIconClassName: 'list__icon list__icon--course',
  iconInternalId: '#icon--course',
  iconExternalId: '#icon--course',
} // DONE! -- ICON OVERWRITE FOR COURSES LINK BLOCK

export const actualityLinkBlockClasses: LinkBlockStyle = {
  ...sharedlinkBlockClasses,
  linkIconClassName: 'list__icon list__icon--actuality',
  iconInternalId: '#icon--actualiteiten',
  iconExternalId: '#icon--actualiteiten',
} // DONE! -- ICON OVERWRITE FOR ACTUALITIES LINK BLOCK

export const linkListBlockNestedClasses: LinkBlockNestedStyle = {
  blockClassName: 'block--links block--nested block--links-nested',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner',
  contentClassName: 'block__content',
  listClassName: 'block__list links',
  itemClassName: 'list__item',
  itemWithLinkClassName: 'list__link',
  itemWithoutLinkClassName: 'list__link',
  linkInnerClassName: 'list__link-inner',
  linkLabelClassName: 'list__title title--xs',
  linkTextClassName: 'list__subtitle',
  linkIconClassName: 'list__icon',
  iconInternalId: '#icon--link',
  iconExternalId: '#icon--external',
} // DONE!

export const downloadBlockClasses: DownloadBlockStyle = {
  blockClassName: 'block block--downloads',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--s gutter--m spacing--m',
  headerClassName: 'block__header block__header--intro',
  titleClassName: 'block__title title--m',
  introClassName: 'block__intro text--l',
  contentClassName: 'block__content',
  listClassName: 'block__list downloads',
  itemClassName: 'list__item',
  linkClassName: 'list__link',
  linkInnerClassName: 'list__link-inner',
  linkLabelClassName: 'list__title title--s',
  linkTextClassName: 'list__subtitle',
  linkIconClassName: 'list__icon',
  iconId: '#icon--download',
} // DONE!

const sharedlinkGridBlockClasses = (
  withImages: boolean
): LinkGridBlockStyle => ({
  blockClassName: withImages == true
    ? 'block block--linkgrid linkgrid--image'
    : 'block block--linkgrid linkgrid--plain',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--m gutter--m spacing--m',
  headerClassName: 'block__header block__header--intro',
  titleClassName: 'block__title title--m',
  introClassName: 'block__intro text--l',
  contentClassName: 'block__content',
  emptyClassName: 'block__empty',
  listClassName: 'block__list grid',
  gridArticleClassName: 'grid__article',
  gridItemClassName: 'grid__item',
  gridWithLinkClassName: 'grid__inner grid__link',
  gridWithoutLinkClassName: 'grid__inner',
  gridContentClassName: 'grid__content',
  gridHeaderClassName: 'grid__header',
  gridLabelClassName: 'grid__label label--m',
  gridTitleClassName: 'grid__title title--s',
  gridIntroClassName: 'grid__intro',
  gridVisualClassName: 'grid__visual',
  gridImageClassName: 'grid__image',
  gridFauxLinkWrapperClassName: 'sr-text',
  gridFauxLinkClassName: 'undefined',
  gridFauxLinkLabelClassName: 'undefined',
  gridFauxLinkIconClassName: 'undefined',
  iconFauxLinkId: 'undefined',
  footerClassName: 'block__footer block__clickables',
  clickableWrapperClassName: 'block__clickable',
  clickableClassName: 'button button--primary',
  clickableIconClassName: 'undefined',
  clickableLabelClassName: 'button__label',
  iconId: 'undefined',
}) // DONE!

export const linkGridBlockClasses = (
  withImages: boolean
): LinkGridBlockStyle => ({
  ...sharedlinkGridBlockClasses(withImages),
  clickableClassName: 'button button--primary',
}) // DONE!

export const relatedActualitiesGridBlockClasses = (
  withImages: boolean
): LinkGridBlockStyle => ({
  ...sharedlinkGridBlockClasses(withImages),
  clickableClassName: 'button button--secondary',
}) // DONE!

export const expandBlockClasses: ExpandBlockStyle = {
  blockClassName: 'block block--expand',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--m gutter--m spacing--m',
  headerClassName: 'block__header block__header--intro',
  titleClassName: 'block__title title--m',
  introClassName: 'block__intro text--l',
  contentClassName: 'block__content',
  listClassName: 'block__list expand',
  expandItemClassName: 'expand__item',
  expandButtonClassName: 'expand__button',
  expandTitleClassName: 'expand__title title--s',
  expandSubtitleClassName: 'expand__subtitle text--xs',
  expandIconClassName: 'expand__icon',
  expandContentClassName: 'expand__content',
  expandTextClassName: 'expand__text text--styled',
  footerClassName: 'block__footer block__clickables',
  clickableWrapperClassName: 'block__clickable',
  clickableClassName: 'button button--primary',
  clickableLabelClassName: 'button__label',
  clickableIconClassName: 'undefined',
  iconId: 'undefined'
} // DONE!

export const advertisementBlockStyle: AdvertisementBlockStyle = {
  blockClassName: 'block block--advert',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--m gutter--m spacing--m',
  blockWrapper: 'block--advert-wrapper',
  linkContainer: 'block--advert-link__container',
}

export const mediaBlockClasses: MediaBlockStyle = {
  blockClassName: 'block block--media',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--m gutter--m spacing--m',
  headerClassName: 'block__header block__header--intro',
  titleClassName: 'block__title title--m',
  introClassName: 'block__intro text--l',
  contentClassName: 'block__content',
  figureContentWidthClassName: 'block__figure figure--cw',
  figureFullWidthClassName: 'block__figure figure--fw',
  figureImageClassName: 'figure--image',
  figureVideoClassName: 'figure--video',
  mediaClassName: 'block__media',
  imageClassName: 'block__image',
  imageNonExpandableClassName: 'block__image--small',
  pictureClassName: 'undefined',
  figCaptionClassName: 'block__figcaption text--xs',
  captionClassName: 'block__caption',
  sourceClassName: 'block__source',
  cookiesClassName: 'block__cookies',
  cookiesInnerClassName: 'block__cookies-inner',
  cookiesTextClassName: 'block__cookies-text',
  cookiesClickableClassName: 'block__cookies-action button button--secondary',
} // DONE!

export const mediaBlockNestedClasses: MediaBlockNestedStyle = {
  blockClassName: 'block--media block--nested block--media-nested',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner',
  contentClassName: 'block__content',
  mediaClassName: 'block__media',
  figureClassName: 'block__figure figure--cw',
  figureImageClassName: 'figure--image',
  figureVideoClassName: 'figure--video',
  videoClassName: 'block__video',
  imageClassName: 'block__image',
  imageNonExpandableClassName: 'block__image--small',
  pictureClassName: 'undefined',
  figCaptionClassName: 'block__figcaption text--xs',
  captionClassName: 'block__caption',
  sourceClassName: 'block__source',
  cookiesClassName: 'cookies',
  cookiesInnerClassName: 'cookies__inner gutter--m spacing--s bg--black',
  cookiesTextClassName: 'cookies__text text--xs text--styled',
  cookiesClickableClassName: 'cookies__action button button--primary-inverted',
} // DONE!

export const textBlockNestedClasses: TextBlockNestedStyle = {
  blockClassName: 'block--text block--text-nested block--nested',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner',
  contentClassName: 'block__content',
  textClassName: 'block__text text--styled',
} // DONE!

export const quoteBlockClasses: Fun<boolean, QuoteBlockStyle> = (hasImage) => ({
  blockClassName: `block block--quote ${
    hasImage ? 'quote--image' : 'quote--text'
  }`,
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--s spacing--m gutter--m',
  contentClassName: 'block__content',
  quoteClassName: 'block__quote',
  quoteTextClassName: 'block__text title--m',
  authorClassName: 'block__author title--xs',
  footerClassName: 'block__footer block__clickables text--xs',
  clickableWrapperClassName: 'block__clickable',
  clickableClassName: 'button button--secondary',
  clickableLabelClassName: 'button__label',
  clickableIconClassName: 'undefined',
  visualClassName: 'block__visual fit-object',
  imageClassName: 'block__image',
  imageNonExpandableClassName: 'block__image--small',
  pictureClassName: 'undefined',
}) // DONE!

export const quoteBlockNestedClasses: QuoteBlockNestedStyle = {
  blockClassName: 'block--quote block--nested block--quote-nested',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner',
  contentClassName: 'block__content gutter--mx',
  quoteClassName: 'block__quote',
  quoteTextClassName: 'block__text title--m',
  authorClassName: 'block__author title--xs',
} // DONE!

export const cookiebarClasses: CookiebarStyle = {
  cookiebarClassName: 'cookiebar text--xs bg--black',
  cookiebarOuterClassName: 'cookiebar__outer container--l gutter--m spacing--s',
  cookiebarInnerClassName: 'cookiebar__inner',
  cookiebarTextClassName: 'cookiebar__text',
  cookiebarTitleClassName: 'cookiebar__title title--s',
  cookiebarIntroClassName: 'cookiebar__intro text--styled',
  cookiebarFooterClassName: 'cookiebar__footer',
  cookiebarClickableWrapperClassName: 'cookiebar__action',
  cookiebarClickablePrimaryClassName: 'button button--primary-inverted',
  cookiebarClickableSecondaryClassName: 'button button--secondary-inverted',
  cookiebarClickableLabelClassName: 'button__label',
  cookiebarClickableIconClassName: 'undefined',
  cookiebarFormClassName: 'cookiebar__form',
  cookiebarExpandClassName: 'cookiebar__expand',
  cookiebarExpandLabelClassName: 'expand__label',
  cookiebarExpandIconClassName: 'expand__icon',
  cookiebarFormListClassName: 'cookiebar__list',
  cookiebarFormItemClassName: 'cookiebar__item',
  expandIconId: '#icon--chevron',
  iconId: 'undefined',
}

export const cookiePlaceholderClasses: CookiePlaceholderStyle = {
  cookiesClassName: 'cookies',
  cookiesInnerClassName: 'cookies__inner gutter--m spacing--s bg--black',
  cookiesTextClassName: 'cookies__text text--xs text--styled',
  cookiesClickableClassName: 'cookies__action button button--primary-inverted',
  cookiesClickableLabelClassName: 'button__label',
  cookiesClickableIconClassName: 'undefined',
  // iconId: '#icon--arrow',
}
// ----- CUSTOM BUILDING BLOCKS -----

export const relatedContentBlockClasses: RelatedContentBlockStyle = {
  blockClassName: 'block block--related',
  blockWithHeaderClassName: 'block--has-header',
  blockWithLinkClassName: 'block--has-link',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--m gutter--m',
  headerClassName: 'block__header',
  titleClassName: 'block__title title--l',
  introClassName: 'block__intro',
  contentClassName: 'block__content',
  listClassName: 'block__list grid',
  gridArticleClassName: 'grid__article',
  gridItemClassName: 'grid__item',
  gridLinkClassName: 'grid__inner grid__link',
  gridContentClassName: 'grid__content',
  gridHeaderClassName: 'grid__header',
  gridLabelClassName: 'grid__label label--m',
  gridTitleClassName: 'grid__title title--m',
  gridIntroClassName: 'grid__intro',
  gridVisualClassName: 'grid__visual container fit-object',
  gridImageClassName: 'grid__image',
  gridDateClassName: 'grid__date typo--xs',
  gridFauxLinkWrapperClassName: 'grid__fauxlink',
  gridFauxLinkClassName: 'fauxlink',
  gridFauxLinkLabelClassName: 'fauxlink__label',
  footerClassName: 'block__footer',
  clickableWrapperClassName: 'clickable',
  clickableClassName: 'link--arrowed',
  clickableIconClassName: 'link__icon',
  clickableLabelClassName: 'link__label',
  iconId: '#icon--arrow',
}

export const incompanyBlockClasses: IncompanyBlockStyle = {
  blockClassName: 'block block--text block--incompany',
  inviewClassName: 'block--inview',
  innerClassName: 'block__inner container--s gutter--m spacing--m',
  headerClassName: 'block__header',
  titleClassName: 'block__title title--m',
  introClassName: 'block__intro text--styled text--l',
  contentClassName: 'block__content',
  textClassName: 'block__text text--styled',
  footerClassName: 'block__footer block__clickables',
  clickableWrapperClassName: 'block__clickable',
  clickablePrimaryClassName: 'button button--primary',
  clickableLabelClassName: 'button__label'
} // DONE!
